// Drag-and-drop screenshot uploader used by the Help Articles editor.
// Uploads to `POST /api/help-articles/screenshot` (multipart) which stores the
// bytes in GridFS and returns a signed URL. Also renders a preview + clear
// button for the currently-attached screenshot.

import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, ImageOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const MAX_MB = 3;
const ALLOWED = ["image/png", "image/jpeg", "image/webp", "image/gif"];

/**
 * @param {string} url    - current screenshot URL (may be empty)
 * @param {(next: string) => void} onChange - fires with new URL (or "" when cleared)
 * @param {string} testid - forwarded to the wrapper
 */
export function ScreenshotUploader({ url, onChange, testid = "screenshot-upload" }) {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error("Screenshot must be PNG, JPEG, WebP or GIF"); return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Screenshot must be under ${MAX_MB} MB`); return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/help-articles/screenshot", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Screenshot uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";   // reset so the same file can be re-picked
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const clear = () => onChange?.("");

  // ---------- attached state: show thumbnail + swap/clear ----------
  if (url) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2 flex items-start gap-3"
           data-testid={testid}>
        <img src={url} alt="Screenshot preview"
             className="h-20 w-32 rounded object-cover border border-slate-200 bg-white shrink-0"
             onError={(e) => { e.target.style.display = "none"; }} />
        <div className="min-w-0 flex-1 text-xs">
          <div className="font-medium text-slate-700 flex items-center gap-1">
            Screenshot attached
          </div>
          <div className="mt-0.5 truncate text-slate-400 font-mono text-[10px]">{url}</div>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={() => inputRef.current?.click()}
                    data-testid={`${testid}-replace`}
                    disabled={uploading}
                    className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50">
              {uploading ? <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> : null}
              Replace
            </button>
            <button type="button" onClick={clear}
                    data-testid={`${testid}-clear`}
                    className="rounded px-2 py-1 text-[11px] font-medium text-slate-500 hover:text-red-600">
              <X className="mr-0.5 inline h-3 w-3" /> Remove
            </button>
          </div>
        </div>
        <input ref={inputRef} type="file" accept={ALLOWED.join(",")}
               onChange={onFilePick} className="hidden" />
      </div>
    );
  }

  // ---------- empty state: drag-drop or click ----------
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => !uploading && inputRef.current?.click()}
      data-testid={testid}
      className={`cursor-pointer rounded-md border-2 border-dashed p-4 text-center text-xs
        transition-colors
        ${drag ? "border-primary bg-primary/5 text-primary"
               : "border-slate-200 bg-slate-50 text-slate-500 hover:border-slate-300 hover:bg-slate-100"}`}
    >
      {uploading ? (
        <div className="flex items-center justify-center gap-2 text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
        </div>
      ) : (
        <>
          <Upload className="mx-auto mb-1 h-5 w-5" />
          <div className="font-medium">Drop a screenshot here, or click to browse</div>
          <div className="mt-0.5 text-[10px] text-slate-400 flex items-center justify-center gap-1">
            <ImageOff className="h-2.5 w-2.5" /> PNG, JPEG, WebP or GIF · max {MAX_MB} MB
          </div>
        </>
      )}
      <input ref={inputRef} type="file" accept={ALLOWED.join(",")}
             onChange={onFilePick} className="hidden" />
    </div>
  );
}
